import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { FaSearch, FaFilter, FaCalendarAlt } from 'react-icons/fa';
import { CustomInputBox1, CustomButton3 } from '../../Templates';
import 'react-datepicker/dist/react-datepicker.css';
import '../styles/EventFilters.css';

/**
 * Component for filtering events by various criteria
 * 
 * @param {Object} props
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.onFiltersChange - Function to call when filters change
 */
function EventFilters({ filters, onFiltersChange }) {
  const { t } = useTranslation('common');
  const [isExpanded, setIsExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState({
    searchTerm: filters.searchTerm || '',
    dateFrom: filters.dateFrom || null,
    dateTo: filters.dateTo || null,
    category: filters.category || 'all',
  });
  
  // Event categories
  const categories = [
    { value: 'all', label: t('events.categories.all') },
    { value: 'music', label: t('events.categories.music') },
    { value: 'sports', label: t('events.categories.sports') },
    { value: 'culture', label: t('events.categories.culture') },
    { value: 'education', label: t('events.categories.education') },
    { value: 'food', label: t('events.categories.food') },
    { value: 'nightlife', label: t('events.categories.nightlife') },
    { value: 'community', label: t('events.categories.community') },
    { value: 'other', label: t('events.categories.other') },
  ];

  // Update local state when input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLocalFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle date changes
  const handleDateChange = (date, field) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: date
    }));
  };

  // Apply filters
  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
    setIsExpanded(false);
  };

  // Reset filters
  const handleResetFilters = () => {
    const resetFilters = {
      searchTerm: '',
      dateFrom: null,
      dateTo: null,
      category: 'all',
    };
    
    setLocalFilters(resetFilters);
    onFiltersChange(resetFilters);
    setIsExpanded(false);
  };

  return (
    <div className="event-filters">
      <div className="search-bar">
        <div className="search-input-container">
          <FaSearch className="search-icon" />
          <CustomInputBox1
            type="text"
            name="searchTerm"
            placeholder={t('events.search_placeholder')}
            value={localFilters.searchTerm}
            onChange={handleInputChange}
            onKeyPress={(e) => e.key === 'Enter' && handleApplyFilters()}
          />
        </div>
        
        <button 
          className={`filter-toggle ${isExpanded ? 'active' : ''}`}
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          aria-label={t('events.toggle_filters')}
        >
          <FaFilter />
          <span>{t('events.filters')}</span>
        </button>
      </div>
      
      {isExpanded && (
        <div className="advanced-filters">
          <div className="filter-row">
            <div className="filter-group">
              <label>{t('events.date_from')}</label>
              <div className="date-picker-container">
                <FaCalendarAlt className="date-icon" />
                <DatePicker
                  selected={localFilters.dateFrom}
                  onChange={(date) => handleDateChange(date, 'dateFrom')}
                  placeholderText={t('events.select_date')}
                  dateFormat="dd/MM/yyyy"
                  minDate={new Date()}
                  className="custom-date-picker"
                />
              </div>
            </div>
            
            <div className="filter-group">
              <label>{t('events.date_to')}</label>
              <div className="date-picker-container">
                <FaCalendarAlt className="date-icon" />
                <DatePicker
                  selected={localFilters.dateTo}
                  onChange={(date) => handleDateChange(date, 'dateTo')}
                  placeholderText={t('events.select_date')}
                  dateFormat="dd/MM/yyyy"
                  minDate={localFilters.dateFrom || new Date()}
                  className="custom-date-picker"
                />
              </div>
            </div>
            
            <div className="filter-group">
              <label>{t('events.category')}</label>
              <select 
                name="category" 
                value={localFilters.category}
                onChange={handleInputChange}
                className="category-select"
              >
                {categories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="filter-actions">
            <CustomButton3
              label={t('buttons.reset')}
              onClick={handleResetFilters}
              className="reset-button"
            />
            <CustomButton3
              label={t('buttons.apply')}
              onClick={handleApplyFilters}
              className="apply-button"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default EventFilters;